import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  FiMoreVertical, FiX, FiDollarSign,
  FiHash, FiCreditCard, FiCheckCircle, FiEye, FiCalendar, FiFileText,
} from "react-icons/fi";
import { MdTrendingUp, MdAttachMoney } from "react-icons/md";
import { FaRupeeSign } from "react-icons/fa";
import AppContext from "../../../context/AppContext.js";
import RentalAdminlayout from "../../../layout/equipmentrentaladmin/RentalAdminLayout.js";
import "../../../assets/css/equipmentrentaladmin/earnings/Earnings.css";
import AppPagination from "../../../components/general/pagination/Pagination.js";

const Earnings = () => {
  const { baseUrl , authToken} = useContext(AppContext);
  const rentaladminId = JSON.parse(localStorage.getItem("LoggedInRental")).id;

  // ── Modal States ──────────────────────────────────────────────────────────
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount,    setWithdrawAmount]    = useState("");
  const [paymentMethod,     setPaymentMethod]     = useState("");
  const [accountTitle,      setAccountTitle]      = useState("");
  const [accountNumber,     setAccountNumber]     = useState("");
  const [bankName,          setBankName]          = useState("");
  const [remarks,           setRemarks]           = useState("");
  const [amountError,       setAmountError]       = useState("");

  // ── View Detail Modal + Dropdown States ─────────────────────────────────
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  // ── Pagination ────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  // ── Data States ───────────────────────────────────────────────────────────
  const [withdrawals, setWithdrawals] = useState([]);

  const [earningsSummary, setEarningsSummary] = useState({
    totalEarning:          0,
    platformServiceCharge: 0,
    netEarning:            0,
    requestedPayout:       0,
    completedPayout:       0,
    availableBalance:      0,
  });
  const [summaryLoading, setSummaryLoading] = useState(true);

  // ✅ Chart — backend se aayega
  const [revenueData,  setRevenueData]  = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  // ✅ Utilization — backend se aayega
  const [utilizationData, setUtilizationData] = useState({
    total:       0,
    active:      0,
    rented:      0,
    idle:        0,
    maintenance: 0,
  });
  const [utilizationLoading, setUtilizationLoading] = useState(true);

  // ── API Calls ─────────────────────────────────────────────────────────────
  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const viewPayouts = () => {
    axios.post(`${baseUrl}/rentaladmin/payout/view`, {
      srrentaladmin_id: rentaladminId
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
    .then((res) => {
      if (res.data.success) setWithdrawals(res.data.requests);
    })
    .catch((err) => console.log(err));
  };

  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const getEarningsSummary = () => {
    setSummaryLoading(true);
    axios.post(`${baseUrl}/rentaladmin/earnings/summary`, {
      srrentaladmin_id: rentaladminId
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
    .then((res) => {
      if (res.data.success) setEarningsSummary(res.data.summary);
    })
    .catch((err) => console.log(err))
    .finally(() => setSummaryLoading(false));
  };

  // ✅ Chart data — backend se
  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const getRevenueChartData = () => {
    setChartLoading(true);
    axios.post(`${baseUrl}/rentaladmin/earnings/monthlyrevenue`, {
      srrentaladmin_id: rentaladminId
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
    .then((res) => {
      if (res.data.success && Array.isArray(res.data.chart)) {
        setRevenueData(res.data.chart);
      } else {
        setRevenueData([]);
      }
    })
    .catch((err) => {
      console.log(err);
      setRevenueData([]);
    })
    .finally(() => setChartLoading(false));
  };

  // ✅ Utilization — backend se
  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const getUtilizationData = () => {
    setUtilizationLoading(true);
    axios.post(`${baseUrl}/rentaladmin/equipmentmanagement/utilization`,
      {
      srrentaladmin_id: rentaladminId
    },
    {
      headers: { Authorization: `Bearer ${authToken}` }
    })
    .then((res) => {
      if (res.data.success && res.data.utilization) {
        setUtilizationData({
          total:       res.data.utilization.total       || 0,
          active:      res.data.utilization.active      || 0,
          rented:      res.data.utilization.rented      || 0,
          idle:        res.data.utilization.idle        || 0,
          maintenance: res.data.utilization.maintenance || 0,
        });
      }
    })
    .catch((err) => console.log(err))
    .finally(() => setUtilizationLoading(false));
  };

  useEffect(() => {
    viewPayouts();
    getEarningsSummary();
    getRevenueChartData();
    getUtilizationData();
  }, []);

  // ── Close dropdown on outside click ─────────────────────────────────────
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".earn-actions-wrapper")) setActiveMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ── Payout Request ────────────────────────────────────────────────────────
  const payoutRequest = () => {
    if (!withdrawAmount || !paymentMethod) return;
    if (Number(withdrawAmount) > earningsSummary.availableBalance) {
      setAmountError(
        `You can only withdraw up to Rs ${earningsSummary.availableBalance.toLocaleString()}`
      );
      return;
    }

    const formData = new FormData();
    formData.append("srrentaladmin_id", rentaladminId);
    formData.append("amount",           withdrawAmount);
    formData.append("paymentmethod",
      paymentMethod === "jazzcash"  ? "JazzCash"  :
      paymentMethod === "easypaisa" ? "EasyPaisa" : "Bank"
    );
    formData.append("accounttitle",  accountTitle);
    formData.append("accountnumber", accountNumber);
    formData.append("bankname",      bankName);
    formData.append("remarks",       remarks);

    axios.post(`${baseUrl}/rentaladmin/payout/request`, formData, {
      headers: { "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${authToken}`
       },
    })
    .then(() => {
      setWithdrawAmount(""); setPaymentMethod(""); setAccountTitle("");
      setAccountNumber(""); setBankName(""); setRemarks("");
      setAmountError(""); setCurrentPage(1);
      setShowWithdrawModal(false);
      viewPayouts();
      getEarningsSummary();
    })
    .catch((err) => console.log(err));
  };

  // ── View Detail ───────────────────────────────────────────────────────────
  const handleViewDetail = (w) => {
    setSelectedWithdrawal(w);
    setShowDetailModal(true);
    setActiveMenu(null);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedWithdrawal(null);
  };

  const getMethodEmoji = (method) => {
    if (method === "JazzCash")  return "📱";
    if (method === "EasyPaisa") return "📲";
    if (method === "Bank")      return "🏦";
    return "💳";
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages   = Math.ceil(withdrawals.length / rowsPerPage);
  const indexOfLast  = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRows  = withdrawals.slice(indexOfFirst, indexOfLast);

  // ── Chart Y-axis ──────────────────────────────────────────────────────────
  const maxRevenue = revenueData.length
    ? Math.max(...revenueData.map((d) => Number(d.revenue) || 0))
    : 0;
  const yAxisMax   = Math.max(200000, Math.ceil((maxRevenue * 1.2) / 40000) * 40000);
  const yAxisTicks = Array.from(
    { length: yAxisMax / 40000 + 1 },
    (_, i) => i * 40000
  );

  // ── Donut chart stroke calc ───────────────────────────────────────────────
  // Circumference = 2 * π * r = 2 * 3.14159 * 70 ≈ 439.8 ≈ 440
  const CIRCUMFERENCE = 440;
  const activeStroke      = (utilizationData.active      / 100) * CIRCUMFERENCE;
  const maintenanceStroke = (utilizationData.maintenance / 100) * CIRCUMFERENCE;

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <RentalAdminlayout>
      <div className="earn-page">

        {/* ── Page Header ── */}
        <div className="earn-page-header">
          <h2 className="earn-page-title">Earnings</h2>
          <button
            className="earn-withdraw-btn"
            onClick={() => setShowWithdrawModal(true)}
          >
            Withdraw Now
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div className="earn-stats-grid">
          {[
            { icon: <FaRupeeSign />,   label: 'Total Earning',            value: earningsSummary.totalEarning          },
            { icon: <FiHash />,        label: 'Requested Payout',         value: earningsSummary.requestedPayout       },
            { icon: <FiCheckCircle />, label: 'Completed Payout',         value: earningsSummary.completedPayout       },
            { icon: <MdTrendingUp />,  label: 'Available Balance',        value: earningsSummary.availableBalance      },
            { icon: <MdAttachMoney />, label: 'Platform Service Charges', value: earningsSummary.platformServiceCharge },
          ].map((card, i) => (
            <div key={i} className="stat-card">
              <div className="stat-icon">{card.icon}</div>
              <div className="stat-info">
                <h3>Rs {summaryLoading ? "..." : Number(card.value).toLocaleString()}</h3>
                <p>{card.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className="rep-charts-grid">

          {/* ── Revenue Line Chart ── */}
          <div className="rep-chart-card">
            <div className="chart-header">
              <div className="chart-title-section">
                <h3 className="chart-title">Equipment Rental Revenue</h3>
                <p className="chart-subtitle">
                  Financial performance over the last 6 months
                </p>
              </div>
              <button className="chart-menu-btn">
                <FiMoreVertical size={20} />
              </button>
            </div>

            <div className="chart-container">
              {chartLoading ? (
                <div style={{ padding: "60px 0", textAlign: "center", color: "#999", fontSize: "13px" }}>
                  Loading chart...
                </div>
              ) : revenueData.length > 0 ? (
                <ResponsiveContainer width="100%" height={360}>
                  {/* ✅ dataKey="month" — backend 'month' field bhejta hai */}
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                    <XAxis
                      dataKey="month"
                      stroke="#999"
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      domain={[0, yAxisMax]}
                      ticks={yAxisTicks}
                      tickFormatter={(v) => `Rs${v / 1000}k`}
                    />
                    <Tooltip
                      formatter={(v) => [`Rs ${Number(v).toLocaleString()}`, "Revenue"]}
                      contentStyle={{
                        background: "#FFF",
                        border: "1px solid #E5E5E5",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#E5A512"
                      strokeWidth={3}
                      name="Revenue"
                      dot={{ fill: "#E5A512", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ padding: "60px 0", textAlign: "center", color: "#999", fontSize: "13px" }}>
                  No revenue data available yet
                </div>
              )}

              <div className="chart-legend">
                <div className="legend-item">
                  <span className="legend-dot revenue" />
                  <span className="legend-label">Revenue</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Equipment Utilization Donut ── */}
          <div className="rep-chart-card utilization-card">
            <div className="chart-header">
              <div className="chart-title-section">
                <h3 className="chart-title">Equipment Utilization</h3>
                <p className="chart-subtitle">
                  Current status of all rental machinery
                  {!utilizationLoading && utilizationData.total > 0 &&
                    ` (${utilizationData.total} total)`
                  }
                </p>
              </div>
            </div>

            <div className="utilization-container">
              <div className="donut-chart">
                <svg viewBox="0 0 200 200" className="donut-svg">
                  {/* Background circle */}
                  <circle
                    cx="100" cy="100" r="70"
                    fill="none" stroke="#E5E5E5" strokeWidth="20"
                  />
                  {/* ✅ Active arc — backend se percentage */}
                  <circle
                    cx="100" cy="100" r="70"
                    fill="none" stroke="#E5A512" strokeWidth="20"
                    strokeDasharray={`${activeStroke} ${CIRCUMFERENCE}`}
                    transform="rotate(-90 100 100)"
                    strokeLinecap="round"
                  />
                  {/* ✅ Maintenance arc */}
                  <circle
                    cx="100" cy="100" r="70"
                    fill="none" stroke="#F44336" strokeWidth="20"
                    strokeDasharray={`${maintenanceStroke} ${CIRCUMFERENCE}`}
                    strokeDashoffset={`-${activeStroke}`}
                    transform="rotate(-90 100 100)"
                    strokeLinecap="round"
                  />
                  {/* Center text */}
                  <text x="100" y="95"  textAnchor="middle" className="donut-percentage">
                    {utilizationLoading ? "..." : `${utilizationData.active}%`}
                  </text>
                  <text x="100" y="115" textAnchor="middle" className="donut-label">
                    Active
                  </text>
                </svg>
              </div>

              <div className="utilization-stats">
                <div className="util-stat-item">
                  <div className="util-stat-header">
                    <span className="util-dot rented" />
                    <span className="util-label">Rented</span>
                  </div>
                  {/* ✅ backend se real percentage */}
                  <div className="util-value">
                    {utilizationLoading ? "..." : `${utilizationData.rented}%`}
                  </div>
                  <div className="util-sublabel">Currently in use</div>
                </div>

                <div className="util-stat-item">
                  <div className="util-stat-header">
                    <span className="util-dot idle" />
                    <span className="util-label">Idle</span>
                  </div>
                  {/* ✅ backend se real percentage */}
                  <div className="util-value">
                    {utilizationLoading ? "..." : `${utilizationData.idle}%`}
                  </div>
                  <div className="util-sublabel">Ready to rent</div>
                </div>

                <div className="util-stat-item">
                  <div className="util-stat-header">
                    <span className="util-dot maintenance" />
                    <span className="util-label">Maintenance</span>
                  </div>
                  {/* ✅ backend se real percentage */}
                  <div className="util-value">
                    {utilizationLoading ? "..." : `${utilizationData.maintenance}%`}
                  </div>
                  <div className="util-sublabel">Under repair</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Withdrawals Table ── */}
        <div className="earn-table-section">
          <div className="earn-section-header">
            <h2 className="earn-section-title">Total Withdrawals</h2>
          </div>

          <div className="earn-table-wrapper">
            <table className="earn-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>DATE</th>
                  <th>AMOUNT</th>
                  <th>PAYMENT METHOD</th>
                  <th>ACCOUNT / BANK DETAILS</th>
                  <th>STATUS</th>
                  <th className="earn-col-action">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.length > 0 ? (
                  currentRows.map((w, index) => (
                    <tr key={w.id}>
                      <td className="earn-serial">{indexOfFirst + index + 1}</td>
                      <td>
                        {w.created_at
                          ? new Date(w.created_at).toLocaleDateString("en-PK")
                          : "—"}
                      </td>
                      <td>
                        <span className="earn-amount">
                          Rs {Number(w.amount).toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <span className="earn-method-badge">
                          {w.paymentmethod === "JazzCash"  && "📱 "}
                          {w.paymentmethod === "EasyPaisa" && "📲 "}
                          {w.paymentmethod === "Bank"      && "🏦 "}
                          {w.paymentmethod}
                        </span>
                      </td>
                      <td className="earn-account-col">
                        {w.paymentmethod === "Bank" ? (
                          <><span className="earn-bank-name">{w.bankname}</span> — {w.accountnumber}</>
                        ) : (
                          w.accountnumber
                        )}
                      </td>
                      <td>
                        <span className="earn-status-badge">
                          <span className="earn-badge-dot" />
                          {w.status}
                        </span>
                      </td>
                      <td className="earn-col-action">
                        <div className="earn-actions-wrapper">
                          <button
                            className="earn-dots-btn"
                            onClick={() =>
                              setActiveMenu(activeMenu === w.id ? null : w.id)
                            }
                          >
                            <FiMoreVertical size={17} />
                          </button>
                          {activeMenu === w.id && (
                            <div className="earn-dropdown-menu">
                              <button
                                className="earn-dropdown-item"
                                onClick={() => handleViewDetail(w)}
                              >
                                <FiEye size={14} className="earn-item-view" />{" "}
                                View Detail
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="earn-empty-row">
                      No withdrawals found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <AppPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={withdrawals.length}
            itemsPerPage={rowsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>

        {/* ── Withdraw Modal ── */}
        {showWithdrawModal && (
          <div
            className="earn-modal-overlay"
            onClick={() => setShowWithdrawModal(false)}
          >
            <div className="earn-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="earn-modal-header-dark">
                <div>
                  <h5 className="earn-modal-header-title">Withdraw Funds</h5>
                  <span className="earn-modal-header-sub">
                    Transfer your earnings to your account
                  </span>
                </div>
                <button
                  className="earn-modal-close-circle"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  <FiX />
                </button>
              </div>

              <div className="earn-modal-body">
                {/* Available balance bar */}
                <div className="earn-modal-status-bar">
                  <div className="earn-sb-left">
                    <FiDollarSign size={15} color="#FDB714" />
                    <span className="earn-sb-label">Available to Withdraw</span>
                  </div>
                  <span className="earn-sb-amount">
                    Rs {earningsSummary.availableBalance.toLocaleString()}
                  </span>
                </div>

                {/* Amount */}
                <p className="earn-modal-section-title">
                  <FiHash size={14} /> Withdrawal Amount
                </p>
                <div className="earn-info-grid">
                  <div className="earn-field-group earn-full-width">
                    <label className="earn-field-label">How Much to Withdraw</label>
                    <input
                      type="number"
                      className="earn-input"
                      placeholder="Enter amount e.g. 5000"
                      max={earningsSummary.availableBalance}
                      min="0"
                      value={withdrawAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        setWithdrawAmount(val);
                        if (Number(val) > earningsSummary.availableBalance) {
                          setAmountError(
                            `You can only withdraw up to Rs ${earningsSummary.availableBalance.toLocaleString()}`
                          );
                        } else {
                          setAmountError("");
                        }
                      }}
                    />
                    {amountError && (
                      <span style={{ color: "#F44336", fontSize: "12px", marginTop: "4px", display: "block" }}>
                        {amountError}
                      </span>
                    )}
                  </div>
                </div>

                {/* Payment Method */}
                <p className="earn-modal-section-title">
                  <FiCreditCard size={14} /> Payment Method
                </p>
                <div className="earn-method-options">
                  {[
                    { key: "jazzcash",  label: "JazzCash",  emoji: "📱" },
                    { key: "easypaisa", label: "EasyPaisa", emoji: "📲" },
                    { key: "bank",      label: "Bank",      emoji: "🏦" },
                  ].map((opt) => (
                    <div
                      key={opt.key}
                      className={`earn-method-option ${paymentMethod === opt.key ? "earn-method-active" : ""}`}
                      onClick={() => {
                        setPaymentMethod(opt.key);
                        setAccountTitle(""); setAccountNumber(""); setBankName("");
                      }}
                    >
                      <div className="earn-method-left">
                        <div className={`earn-radio-dot ${paymentMethod === opt.key ? "selected" : ""}`} />
                        <span className="earn-method-emoji">{opt.emoji}</span>
                        <span className="earn-method-label">{opt.label}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* JazzCash / EasyPaisa fields */}
                {(paymentMethod === "jazzcash" || paymentMethod === "easypaisa") && (
                  <div className="earn-info-grid" style={{ marginTop: "16px" }}>
                    <div className="earn-field-group earn-full-width">
                      <label className="earn-field-label">Account Title</label>
                      <input type="text" className="earn-input" placeholder="Enter account title"
                        value={accountTitle} onChange={(e) => setAccountTitle(e.target.value)} />
                    </div>
                    <div className="earn-field-group earn-full-width">
                      <label className="earn-field-label">
                        {paymentMethod === "jazzcash" ? "JazzCash" : "EasyPaisa"} Account Number
                      </label>
                      <input type="text" className="earn-input"
                        placeholder={`Enter ${paymentMethod === "jazzcash" ? "JazzCash" : "EasyPaisa"} number`}
                        value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
                    </div>
                  </div>
                )}

                {/* Bank fields */}
                {paymentMethod === "bank" && (
                  <div className="earn-info-grid" style={{ marginTop: "16px" }}>
                    <div className="earn-field-group earn-full-width">
                      <label className="earn-field-label">Account Title</label>
                      <input type="text" className="earn-input" placeholder="Enter account title"
                        value={accountTitle} onChange={(e) => setAccountTitle(e.target.value)} />
                    </div>
                    <div className="earn-field-group">
                      <label className="earn-field-label">Bank Name</label>
                      <input type="text" className="earn-input" placeholder="e.g. HBL"
                        value={bankName} onChange={(e) => setBankName(e.target.value)} />
                    </div>
                    <div className="earn-field-group">
                      <label className="earn-field-label">Account Number</label>
                      <input type="text" className="earn-input" placeholder="Enter account number"
                        value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
                    </div>
                  </div>
                )}

                {/* Summary box */}
                {withdrawAmount && paymentMethod && (
                  <div className="earn-financials-box">
                    <div className="earn-fin-row">
                      <span>Withdrawal Amount</span>
                      <span className="earn-fin-pos">
                        Rs {Number(withdrawAmount).toLocaleString()}
                      </span>
                    </div>
                    <div className="earn-fin-row earn-fin-total">
                      <span>You will receive</span>
                      <span>Rs {Number(withdrawAmount).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="earn-modal-footer-list">
                <button
                  className="earn-mf-btn earn-mf-confirm"
                  onClick={payoutRequest}
                  disabled={!!amountError}
                  style={amountError ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                >
                  <FiCheckCircle size={15} /> Confirm Withdrawal
                </button>
                <button
                  className="earn-mf-btn earn-mf-close"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  <FiX size={15} /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── View Detail Modal ── */}
        {showDetailModal && selectedWithdrawal && (
          <div className="earn-modal-overlay" onClick={closeDetailModal}>
            <div className="earn-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="earn-modal-header-dark">
                <div>
                  <h5 className="earn-modal-header-title">Withdrawal Detail</h5>
                  <span className="earn-modal-header-sub">
                    Full breakdown of this withdrawal request
                  </span> 
                </div>
                <button 
                  className="earn-modal-close-circle"
                  onClick={closeDetailModal}
                >
                  <FiX />
                </button>
              </div>

              <div className="earn-modal-body">
                {/* Status bar */}
                <div className="earn-modal-status-bar">
                  <div className="earn-sb-left">
                    <FiDollarSign size={15} color="#FDB714" />
                    <span className="earn-sb-label">
                      Withdrawal Amount
                    </span>
                  </div>
                  <span className="earn-sb-amount">
                    Rs {Number(selectedWithdrawal.amount).toLocaleString()}
                  </span>
                </div>

                {/* Basic info */}
                <p className="earn-modal-section-title">
                  <FiHash size={14} /> Request Information
                </p>
                <div className="earn-info-grid">
                  <div className="earn-field-group">
                    <label className="earn-field-label">Status</label>
                    <span className="earn-status-badge" style={{ display: "inline-flex", marginTop: "4px" }}>
                      <span className="earn-badge-dot" />
                      {selectedWithdrawal.status}
                    </span>
                  </div>
                  <div className="earn-field-group">
                    <label className="earn-field-label">
                      <FiCalendar size={12} style={{ marginRight: 4 }} />
                      Date Requested
                    </label>
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      {selectedWithdrawal.created_at
                        ? new Date(selectedWithdrawal.created_at).toLocaleDateString("en-PK")
                        : "—"}
                    </span>
                  </div>
                </div>

                {/* Payment method detail */}
                <p className="earn-modal-section-title">
                  <FiCreditCard size={14} /> Payment Detail
                </p>
                <div className="earn-info-grid">
                  <div className="earn-field-group earn-full-width">
                    <label className="earn-field-label">Payment Method</label>
                    <span style={{ fontSize: "14px", fontWeight: 600 }}>
                      {getMethodEmoji(selectedWithdrawal.paymentmethod)} {selectedWithdrawal.paymentmethod}
                    </span>
                  </div>

                  {/* JazzCash / EasyPaisa */}
                  {(selectedWithdrawal.paymentmethod === "JazzCash" ||
                    selectedWithdrawal.paymentmethod === "EasyPaisa") && (
                    <>
                      <div className="earn-field-group">
                        <label className="earn-field-label">Account Title</label>
                        <span style={{ fontSize: "14px", fontWeight: 500 }}>
                          {selectedWithdrawal.accounttitle || "—"}
                        </span>
                      </div>
                      <div className="earn-field-group">
                        <label className="earn-field-label">
                          {selectedWithdrawal.paymentmethod} Account Number
                        </label>
                        <span style={{ fontSize: "14px", fontWeight: 500 }}>
                          {selectedWithdrawal.accountnumber || "—"}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Bank */}
                  {selectedWithdrawal.paymentmethod === "Bank" && (
                    <>
                      <div className="earn-field-group">
                        <label className="earn-field-label">Account Title</label>
                        <span style={{ fontSize: "14px", fontWeight: 500 }}>
                          {selectedWithdrawal.accounttitle || "—"}
                        </span>
                      </div>
                      <div className="earn-field-group">
                        <label className="earn-field-label">Bank Name</label>
                        <span style={{ fontSize: "14px", fontWeight: 500 }}>
                          {selectedWithdrawal.bankname || "—"}
                        </span>
                      </div>
                      <div className="earn-field-group earn-full-width">
                        <label className="earn-field-label">Account Number</label>
                        <span style={{ fontSize: "14px", fontWeight: 500 }}>
                          {selectedWithdrawal.accountnumber || "—"}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Remarks, if any */}
                {selectedWithdrawal.remarks && (
                  <>
                    <p className="earn-modal-section-title">
                      <FiFileText size={14} /> Remarks
                    </p>
                    <div className="earn-info-grid">
                      <div className="earn-field-group earn-full-width">
                        <span style={{ fontSize: "14px" }}>{selectedWithdrawal.remarks}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Summary box */}
                <div className="earn-financials-box">
                  <div className="earn-fin-row">
                    <span>Withdrawal Amount</span>
                    <span className="earn-fin-pos">
                      Rs {Number(selectedWithdrawal.amount).toLocaleString()}
                    </span>
                  </div>
                  <div className="earn-fin-row earn-fin-total">
                    <span>Status</span>
                    <span>{selectedWithdrawal.status}</span>
                  </div>
                </div>
              </div>

              <div className="earn-modal-footer-list">
                <button
                  className="earn-mf-btn earn-mf-close"
                  onClick={closeDetailModal}
                >
                  <FiX size={15} /> Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </RentalAdminlayout>
  );
};

export default Earnings;